import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { Chip, EmptyState, InfiniteSentinel, SearchBar, Skeletons, initials } from '../components/kit';
import { usePaginatedList } from '../hooks/usePaginatedList';
import * as contactsApi from '../api/contacts';

const TABS = [
  { label: 'Vendors', value: 'vendor' },
  { label: 'Purchasers', value: 'purchaser' },
  { label: 'Co-agencies', value: 'coagency' },
];

const LIST_FN = {
  vendor: contactsApi.listVendors,
  purchaser: contactsApi.listPurchasers,
  coagency: contactsApi.listCoAgencies,
};

// Inner list — mounted with key={type} so the paginated hook fully resets when the tab changes.
function ContactList({ type, search, mine, onOpen }) {
  // `mine` is only sent when on, so the default request is unchanged.
  const params = useMemo(() => ({ search, mine: mine ? 1 : undefined }), [search, mine]);
  const list = usePaginatedList(LIST_FN[type], { limit: 10, params });

  if (list.loading) return <Skeletons n={4} height={64} />;

  // A failed fetch also leaves `items` empty, and this registry is small enough that a
  // genuinely empty one is normal — so "no contacts" would be a believable lie. The
  // error is drawn before the empty state, never as it.
  if (list.error && !list.items.length) {
    return (
      <>
        <EmptyState title="Could not load contacts" sub={list.error.message || 'Something went wrong reading your registry.'} />
        <div onClick={list.refresh} style={{ alignSelf: 'center', fontSize: 12.5, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', padding: '2px 8px' }}>Retry</div>
      </>
    );
  }

  if (list.empty) {
    return mine
      ? <EmptyState title="Nothing of your own here" sub="You have not registered a contact of this type yet. Tap ＋ to add one." />
      : <EmptyState title="No contacts here" sub="Contacts you register, and the parties already on your own deals, show up here. Tap ＋ to add one." />;
  }

  return (
    <>
      {list.items.map((c) => (
        <div key={c.id} onClick={() => onOpen(c.id)} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer' }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--chip)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: 700, flex: 'none' }}>{initials(c.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {c.phone || 'No phone'}
              {c.code && <> · <span style={{ fontFamily: 'ui-monospace,monospace' }}>{c.code}</span></>}
            </div>
          </div>
          {/* A contact can be in your registry because it sits on your deal while HQ or
          another agent registered it — that row carries its creator, and "Mine"
          filters it out to leave only what you can edit. */}
          {c.is_mine === false && c.creator_name && <Chip label={`by ${c.creator_name}`} tone="brand" />}
          {c.entity_type && <Chip label={c.entity_type} />}
        </div>
      ))}
      <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
    </>
  );
}

export default function ContactsPage() {
  const nav = useNavigate();
  const [type, setType] = useState('vendor');
  const [search, setSearch] = useState('');
  // The whole registry is already this agent's own — what they registered, plus the
  // parties on their own sales, booking forms and letters. "Mine" narrows it further to
  // just what they registered, which is the same set they are allowed to edit.
  const [mine, setMine] = useState(false);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <AppBar back title="Contacts registry" sub="your vendors · purchasers · co-agencies" />

      {/* Same chrome as Booking forms and CID Forms: a scope row plus the shared
          debounced SearchBar, in one surface block under the AppBar. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {TABS.map((t) => {
            const active = type === t.value;
            return (
              <div
                key={t.value}
                onClick={() => setType(t.value)}
                style={{ flex: 1, textAlign: 'center', fontSize: 12.5, fontWeight: 600, padding: '7px 0', borderRadius: 999, cursor: 'pointer', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}
              >
                {t.label}
              </div>
            );
          })}
        </div>
        {/* Search flexes; the owner filter keeps its intrinsic width beside it.
            SearchBar takes no style prop, so it is stretched by its wrapper. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchBar onSearch={setSearch} placeholder="Search name…" />
          </div>
          <div style={{ display: 'flex', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11, padding: 3, flex: 'none' }}>
            {[[false, 'All'], [true, 'Mine']].map(([value, label]) => {
              const on = mine === value;
              return (
                <div
                  key={label}
                  onClick={() => setMine(value)}
                  style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, cursor: 'pointer', background: on ? 'var(--primary)' : 'transparent', color: on ? '#fff' : 'var(--muted)' }}
                >
                  {label}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ContactList key={type} type={type} search={search} mine={mine} onOpen={(id) => nav(`/contacts/${type}/${id}`)} />
        <div style={{ height: 66 }} />
      </div>

      <div onClick={() => nav(`/contacts/${type}/new`)} style={{ position: 'absolute', right: 18, bottom: 18, width: 54, height: 54, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, cursor: 'pointer', boxShadow: '0 8px 20px color-mix(in srgb,var(--primary) 45%,transparent)', zIndex: 5 }}>＋</div>
    </div>
  );
}
